// TypeScript mirror of the universal contract documented in
// `flights/schemas.py` on the backend. Keep field names verbatim — the
// API never wraps responses, it returns these shapes directly.

export type JourneyType = "one_way" | "round_trip" | "multi_city";

// Which upstream serves a search. Sent as `source` on /v1/flights/search/ and
// echoed back as `provider` on every response. Everything after the search is
// pinned to whichever provider served it.
export type FlightSource = "nuitee" | "travclan";

export const FLIGHT_SOURCES: Array<{ value: FlightSource; label: string }> = [
  { value: "nuitee", label: "Nuitee" },
  { value: "travclan", label: "TravClan" },
];

export const DEFAULT_FLIGHT_SOURCE: FlightSource = "nuitee";

// How the provider returned a round trip.
//   "independent" - separate outbound[] + inbound[]; send BOTH result_indices.
//   "coupled"     - returns nested under each outbound as inbound_options[];
//                   send ONLY the chosen inbound_option's result_index.
// TravClan couples international round trips only; Nuitee couples every one.
export type RoundTripStyle = "coupled" | "independent";

export type CabinClass =
  | "economy"
  | "premium_economy"
  | "business"
  | "first"
  | "premium_first"
  | "any";
export type PaxType = "adult" | "child" | "infant";
export type BookingStatus =
  | "draft"
  | "passenger_saved"
  | "priced"
  | "confirmed"
  | "cancelled"
  | "failed";

export interface PaxCount {
  adult: number;
  child: number;
  infant: number;
}

export interface Airport {
  airport_code: string;
  airport_name?: string;
  city_code?: string;
  city_name?: string;
  country_name?: string;
  terminal?: string | null;
  departure_time?: string;
  arrival_time?: string;
}

export interface Airline {
  code: string;
  name: string;
  flight_number: string;
  fare_class?: string;
}

export interface FlightSegment {
  airline: Airline;
  origin: Airport;
  destination: Airport;
  cabin_class: string;
  duration_minutes: number;
  ground_time_minutes: number;
  baggage_allowance?: string;
  cabin_baggage_allowance?: string;
  seats_available?: number;
}

export interface Pricing {
  currency: string;
  base_fare: number;
  service_fee: number;
  tax_and_surcharge?: number;
  published_fare: number;
  final_fare: number;
  previous_total_amount?: number | null;
  price_changed?: boolean;
  markup_percent?: number;
  raw?: Record<string, unknown>;
}

export interface FlightOption {
  result_index: string;
  provider: FlightSource | string;
  is_refundable: boolean;
  stop_count: number;
  fare_name: string;
  fare_class: string;
  airline_remark?: string | null;
  group_id?: string | null;
  pricing: Pricing;
  segments: FlightSegment[];
  inbound_options?: FlightOption[];
  // Backend marks the cheapest non-stop (or cheapest overall) in each list
  // as kaira_picked=true. Partner UIs can render a "picked for you" tag.
  kaira_picked?: boolean;
}

// Pre-computed Cheapest / Fastest / Earliest from the backend.
// `fare` is in the response's display currency; for round-trips it's the
// combined outbound+inbound total.
export interface SortStat {
  fare: number;
  duration_minutes: number;
  departure_time?: string;
  airlines: string[];
}
export interface SortStats {
  cheapest: SortStat;
  fastest: SortStat;
  earliest: SortStat;
}

export interface SearchResponse {
  provider: FlightSource | string;
  trace_id: string;
  currency: string;
  journey_type: JourneyType;
  is_domestic: boolean;
  // Prefer this over deriving the layout from is_domestic — that rule is
  // TravClan-specific. Absent on responses cached before it existed.
  round_trip_style?: RoundTripStyle | null;
  pax_count: PaxCount;
  // Nuitee only: the real offer expiry. TravClan results run a flat 15 min.
  expires_at?: string | null;
  remaining_seconds?: number | null;
  outbound: FlightOption[];
  inbound: FlightOption[];
  sort_stats?: SortStats;
  // Only set when the provider had nothing for this route — the API answers
  // 200 with empty lists and the upstream's own message here.
  detail?: string;
}

export interface ItineraryLeg {
  direction: "outbound" | "inbound" | string;
  stop_count: number;
  segments: FlightSegment[];
}

export interface ItineraryItem {
  item_code: string;
  result_index: string;
  journey_type: JourneyType;
  is_refundable: boolean;
  fare_name: string;
  fare_class: string;
  airline_remark?: string | null;
  legs: ItineraryLeg[];
}

export interface Itinerary {
  booking_id: string;
  provider: FlightSource | string;
  trace_id: string;
  itinerary_code: string;
  status: BookingStatus;
  pnr?: string | null;
  bms_booking_code?: string | null;
  // Nuitee only. `booking_reference` is Nuitee's own ref (FH-YYM-XXXXXXXX) and
  // is NOT a PNR — check-in needs the airline PNR.
  airline_pnrs?: Array<{ airline_code: string | null; pnr: string }>;
  booking_reference?: string | null;
  provider_status?: string | null;
  is_domestic: boolean;
  // True whenever travel documents are mandatory. Nuitee requires them on every
  // passenger including domestic, so do NOT derive this from is_domestic.
  passport_required?: boolean;
  pax_count: PaxCount;
  expires_at?: string;
  remaining_seconds?: number;
  pricing: Pricing;
  itinerary_items: ItineraryItem[];
  pax_rules?: Record<string, unknown>;
  // Set by /passengers/ response
  duplicates_found?: boolean;
  duplicates?: Array<{
    pnr: string;
    status: string;
    bms_booking_code?: string;
    member_code?: string;
    origin_city_code?: string;
    destination_city_code?: string;
    created_at?: string;
  }>;
  // Set by /book/ response
  wallet_debited_inr?: number | null;
}

export interface FareRule {
  airline: string;
  origin: string;
  destination: string;
  rule_text: string;
}

export interface FareRulesResponse {
  provider: FlightSource | string;
  trace_id: string;
  result_index: string;
  expires_at?: string;
  remaining_seconds?: number;
  rules: FareRule[];
}

export interface SSREntry {
  code: string;
  origin: string;
  destination: string;
  amt?: number;
  seat?: string;
}

export interface Passenger {
  title: "Mr" | "Ms" | "Mrs" | "Mstr" | "Miss" | string;
  first_name: string;
  last_name: string;
  gender: "male" | "female" | "other" | string;
  pax_type: PaxType;
  dob?: string;
  is_lead: boolean;
  nationality?: string;
  email?: string;
  contact_number?: string;
  cell_country_code?: string;
  address_line_one?: string;
  address_line_two?: string;
  city?: string;
  country_code?: string;
  country_name?: string;
  passport_number?: string;
  passport_expiry?: string;
  passport_issue_date?: string;
  frequent_flyer_airline_code?: string | null;
  frequent_flyer_number?: string | null;
  gst_company_name?: string | null;
  gst_company_email?: string | null;
  gst_company_address?: string | null;
  gst_company_contact_number?: string | null;
  gst_number?: string | null;
  ssr?: {
    meal?: SSREntry[];
    baggage?: SSREntry[];
    seat?: SSREntry[];
  };
}

// Auth shapes from /flights/auth/me/ and /flights/auth/login/
export interface AuthUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}
export interface AuthPartner {
  id: string;
  name: string;
  contact_email: string;
  currency: string;
  markup_percent: number;
  balance: number;
}
export interface MeResponse {
  user: AuthUser;
  partner: AuthPartner | null;
}

// Backend's BookingSerializer (`/v1/bookings/`) row shape.
// One table serves both verticals: `product` says which of `flight_segments` /
// `hotel_details` carries the itinerary summary, and `passengers` holds the
// travellers either way. Both blobs are JSONFields — typed loosely here since
// the model stores whatever was assigned at booking time.
export interface BookingListItem {
  id: string;
  product: "flight" | "hotel";
  provider: FlightSource | string;
  status: BookingStatus;
  pnr: string | null;
  trace_id: string | null;
  itinerary_code: string | null;
  currency: string;
  amount_charged: string | null;
  wallet_debited_inr: string | null;
  passengers: Array<Record<string, unknown>>;
  flight_segments: Array<Record<string, unknown>>;
  hotel_details: Record<string, unknown>;
  created_at: string;
  modified_at: string;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
